import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from "@tanstack/react-query";
import type {
  DominioCreate,
  DominioUpdate,
  FiltroVencimiento,
  Moneda,
  ProveedorDominioCreate,
  ProveedorDominioUpdate,
  RenovarDominio,
} from "@uirtus/shared";
import { api } from "./api";

export interface Dominio {
  id: string;
  clienteId: string;
  proveedorId: string | null;
  nombre: string;
  fechaRegistro: string | null;
  fechaVencimiento: string;
  autoRenovacion: boolean;
  costoRenovacion: string | null;
  moneda: Moneda;
  cuentaProveedor: string | null;
  activo: boolean;
  recordatoriosActivos: boolean;
  diasAviso: number[];
  correosAviso: string | null;
  notas: string | null;
  diasRestantes: number;
}

export interface DominioLista extends Dominio {
  cliente: { id: string; rfc: string; razonSocial: string };
  proveedor: { id: string; nombre: string } | null;
}

export interface RecordatorioEnviado {
  id: string;
  fechaVencimiento: string;
  diasAntes: number | null;
  destinatarios: string;
  asunto: string;
  estado: "ENVIADO" | "ERROR";
  error: string | null;
  manual: boolean;
  enviadoAt: string;
}

export interface DominioConDetalle extends Dominio {
  cliente: {
    id: string;
    rfc: string;
    razonSocial: string;
    email: string | null;
    telefono: string | null;
    contactos: { id: string; nombre: string; email: string | null; esPrincipal: boolean }[];
  };
  proveedor: { id: string; nombre: string; sitioWeb: string | null } | null;
  recordatorios: RecordatorioEnviado[];
  destinatarios: string[];
}

export interface ProveedorDominio {
  id: string;
  nombre: string;
  sitioWeb: string | null;
  notas: string | null;
  dominios: number;
}

export interface ResumenDominios {
  total: number;
  vencidos: number;
  vencenEn30: number;
  vencenEn60: number;
  urgentes: (Dominio & { cliente: { id: string; razonSocial: string } })[];
}

const CLAVE = ["dominios"] as const;
const CLAVE_PROVEEDORES = ["dominios", "proveedores"] as const;

export interface FiltrosDominios {
  clienteId?: string;
  proveedorId?: string;
  vencimiento?: FiltroVencimiento;
  /** Umbral de "por vencer", en días. */
  dias?: number;
  activo?: "true" | "false" | "todos";
  q?: string;
}

export function useDominios(filtros: FiltrosDominios = {}) {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(filtros)) if (v) p.set(k, String(v));
  const cadena = p.toString();
  return useQuery({
    queryKey: [...CLAVE, "lista", filtros],
    queryFn: () => api<DominioLista[]>(`/dominios${cadena ? `?${cadena}` : ""}`),
  });
}

export const useResumenDominios = () =>
  useQuery({
    queryKey: [...CLAVE, "resumen"],
    queryFn: () => api<ResumenDominios>("/dominios/resumen"),
  });

export const useCorreoDominios = () =>
  useQuery({
    queryKey: [...CLAVE, "correo"],
    queryFn: () => api<{ habilitado: boolean }>("/dominios/correo"),
    staleTime: 5 * 60_000,
  });

export const useDominio = (id: string) =>
  useQuery({
    queryKey: [...CLAVE, id],
    queryFn: () => api<DominioConDetalle>(`/dominios/${id}`),
    enabled: !!id,
  });

export const useProveedoresDominio = () =>
  useQuery({
    queryKey: CLAVE_PROVEEDORES,
    queryFn: () => api<ProveedorDominio[]>("/dominios/proveedores"),
  });

function useMutacion<TDatos, TVars>(
  fn: (vars: TVars) => Promise<TDatos>,
  opciones?: UseMutationOptions<TDatos, Error, TVars>,
) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: fn,
    ...opciones,
    onSuccess: (...args) => {
      // Todo lo de dominios (listas, resumen, proveedores) cuelga de la misma raíz.
      void cliente.invalidateQueries({ queryKey: CLAVE });
      opciones?.onSuccess?.(...args);
    },
  });
}

export const useCrearDominio = () =>
  useMutacion((body: DominioCreate) =>
    api<Dominio>("/dominios", { method: "POST", body }),
  );

export const useActualizarDominio = (id: string) =>
  useMutacion((body: DominioUpdate) =>
    api<Dominio>(`/dominios/${id}`, { method: "PATCH", body }),
  );

export const useRenovarDominio = (id: string) =>
  useMutacion((body: RenovarDominio) =>
    api<Dominio>(`/dominios/${id}/renovar`, { method: "POST", body }),
  );

export const useEnviarRecordatorio = (id: string) =>
  useMutacion(() =>
    api<{ enviado: boolean; destinatarios: string[] }>(`/dominios/${id}/recordatorio`, {
      method: "POST",
    }),
  );

export const useEliminarDominio = (id: string) =>
  useMutacion(() => api<void>(`/dominios/${id}`, { method: "DELETE" }));

export const useCrearProveedor = () =>
  useMutacion((body: ProveedorDominioCreate) =>
    api<ProveedorDominio>("/dominios/proveedores", { method: "POST", body }),
  );

export const useActualizarProveedor = () =>
  useMutacion((v: { id: string; body: ProveedorDominioUpdate }) =>
    api<ProveedorDominio>(`/dominios/proveedores/${v.id}`, { method: "PATCH", body: v.body }),
  );

export const useEliminarProveedor = () =>
  useMutacion((id: string) =>
    api<void>(`/dominios/proveedores/${id}`, { method: "DELETE" }),
  );
